import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Plus, Send, Loader2, CheckCircle2, AlertCircle, Download, FileSignature, Trash2, X } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * B2BContractEngine — DocuSign-backed contract template engine.
 *
 * Founder picks a template (School Licence, Gym Pilot,
 * Co-Branded Distribution), fills the variable fields + signer list,
 * drags the configured offer card into the "Execution Zone" — which
 * POSTs /api/vault/contracts/send and ships the envelope.
 *
 * In stub-mode (DOCUSIGN_ENABLED=false) the backend still returns a
 * deterministic envelope_id and serves a placeholder PDF, so the full
 * flow is exercisable before sandbox credentials are wired.
 */

const PALETTE = {
  coral: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  text: '#2A2235',
  muted: '#5C526A',
  cream: '#FAFAED',
  border: 'rgba(92,82,106,0.10)',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const STATUS_STYLE = {
  sent: { label: 'Sent · awaiting signature', bg: '#FFF3E5', fg: '#9A4A33' },
  in_flight: { label: 'In flight', bg: '#E9EAF7', fg: '#3F4D7A' },
  completed: { label: 'Signed', bg: '#E8F6EF', fg: '#1F5C44' },
  voided: { label: 'Voided', bg: '#F2E4E4', fg: '#7A4040' },
  declined: { label: 'Declined', bg: '#F2E4E4', fg: '#7A4040' },
};

export const B2BContractEngine = () => {
  const [catalogue, setCatalogue] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [draft, setDraft] = useState(null); // { template_key, variables, signers, note }
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [completing, setCompleting] = useState(null);

  const loadCatalogue = useCallback(async () => {
    try {
      const r = await apiClient.request('/api/vault/contracts/templates');
      setCatalogue(r);
    } catch (e) { setError(e.message || 'Failed to load templates'); }
  }, []);

  const loadContracts = useCallback(async () => {
    try {
      const r = await apiClient.request('/api/vault/contracts/');
      setContracts(r.contracts || []);
    } catch { /* swallow */ }
  }, []);

  useEffect(() => { loadCatalogue(); loadContracts(); }, [loadCatalogue, loadContracts]);

  const startDraft = (tpl) => {
    setDraft({
      template_key: tpl.key,
      label: tpl.label,
      description: tpl.description,
      variables: Object.fromEntries((tpl.variables || []).map(v => [v.key, v.default || ''])),
      signers: (tpl.roles || []).map(r => ({ name: '', email: '', role_name: r.role_name, role_label: r.label })),
      note: '',
      template: tpl,
    });
    setError(null);
    setSuccess(null);
  };

  // Listen for `hibff:contract-engine:open-draft` window events fired by
  // the per-investor RecommendOfferButton. Pre-loads the configurator with
  // the backend-suggested template + populated variables/signers.
  useEffect(() => {
    const onOpenDraft = (e) => {
      const detail = (e && e.detail) || {};
      const tplKey = detail.template_key;
      if (!tplKey || !catalogue) return;
      const tpl = (catalogue.templates || []).find(t => t.key === tplKey);
      if (!tpl) return;
      const seededVars = Object.fromEntries((tpl.variables || []).map(v => [v.key, (detail.variables || {})[v.key] || v.default || '']));
      const seededSigners = (tpl.roles || []).map((r, idx) => {
        const incoming = (detail.signers || [])[idx] || {};
        return {
          name: incoming.name || '',
          email: incoming.email || '',
          role_name: r.role_name,
          role_label: r.label,
        };
      });
      setDraft({
        template_key: tpl.key,
        label: tpl.label,
        description: tpl.description,
        variables: seededVars,
        signers: seededSigners,
        note: detail.note || '',
        template: tpl,
        recommendation_reason: detail.reason || null,
      });
      setError(null);
      setSuccess(null);
      // Scroll the configurator into view shortly after render.
      setTimeout(() => {
        try { document.querySelector('[data-testid="b2b-contract-engine"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* noop */ }
      }, 80);
    };
    window.addEventListener('hibff:contract-engine:open-draft', onOpenDraft);
    return () => window.removeEventListener('hibff:contract-engine:open-draft', onOpenDraft);
  }, [catalogue]);

  const updateVariable = (k, v) => setDraft(d => ({ ...d, variables: { ...d.variables, [k]: v } }));
  const updateSigner = (idx, field, val) => setDraft(d => {
    const next = [...d.signers]; next[idx] = { ...next[idx], [field]: val }; return { ...d, signers: next };
  });

  const isDraftReady = draft
    && draft.signers.every(s => s.name.trim() && s.email.trim() && s.role_name)
    && (draft.template.variables || []).every(v => String(draft.variables[v.key] || '').trim() !== '');

  const submit = async () => {
    if (!isDraftReady) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiClient.request('/api/vault/contracts/send', {
        method: 'POST',
        body: JSON.stringify({
          template_key: draft.template_key,
          variables: draft.variables,
          signers: draft.signers.map(s => ({ name: s.name, email: s.email, role_name: s.role_name })),
          note: draft.note || null,
        }),
      });
      setSuccess(`Envelope ${res.envelope_id} dispatched (${res.provider}).`);
      setDraft(null);
      loadContracts();
    } catch (e) {
      setError(e.message || 'Dispatch failed');
    }
    setSending(false);
  };

  const onDragStart = (e) => {
    if (!isDraftReady) { e.preventDefault(); return; }
    e.dataTransfer.setData('text/plain', 'hibff-contract');
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => { if (isDraftReady && !sending) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragging(true); } };
  const onDragLeave = () => setDragging(false);
  const onDrop = (e) => { e.preventDefault(); setDragging(false); if (isDraftReady && !sending) submit(); };

  const markCompleted = async (cid) => {
    setCompleting(cid);
    try {
      await apiClient.request(`/api/vault/contracts/${cid}/mark-completed`, { method: 'POST' });
      await loadContracts();
    } catch (e) { setError(e.message || 'Mark-completed failed'); }
    setCompleting(null);
  };

  const downloadPdf = async (cid, label) => {
    try {
      const url = `${apiClient.baseUrl}/api/vault/contracts/${cid}/pdf`;
      const token = apiClient.getToken();
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `HiBFF-${(label || 'contract').replace(/\s+/g, '-')}-${cid.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 30000);
    } catch (e) {
      setError(e.message || 'Download failed');
    }
  };

  return (
    <div className="rounded-2xl p-5 lg:p-6" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.coral}` }} data-testid="b2b-contract-engine">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: PALETTE.coral + '22' }}>
            <FileSignature size={16} style={{ color: '#9A4A33' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: PALETTE.text }}>B2B Contract Engine</h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>
              DocuSign templates · {catalogue?.docusign_enabled ? 'production sandbox' : <span className="font-semibold" style={{ color: '#9A4A33' }}>STUB MODE — wire DOCUSIGN_INTEGRATION_KEY to go live</span>}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg flex items-center gap-2 text-xs" style={{ background: '#F2E4E4', color: '#7A4040' }} data-testid="contract-engine-error">
          <AlertCircle size={12} /> {error}
        </div>
      )}
      {success && (
        <div className="mb-3 px-3 py-2 rounded-lg flex items-center gap-2 text-xs" style={{ background: '#E8F6EF', color: '#1F5C44' }} data-testid="contract-engine-success">
          <CheckCircle2 size={12} /> {success}
        </div>
      )}

      {/* TEMPLATE CARDS */}
      {!draft && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="contract-template-grid">
          {(catalogue?.templates || []).map(tpl => (
            <button key={tpl.key} onClick={() => startDraft(tpl)} className="text-left p-4 rounded-xl transition-transform hover:scale-[1.01]" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}` }} data-testid={`contract-template-${tpl.key}`}>
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: '#9A4A33' }}>
                <FileText size={11} /> {tpl.key}
              </div>
              <div className="text-sm font-bold" style={{ color: PALETTE.text }}>{tpl.label}</div>
              <div className="text-xs mt-1" style={{ color: PALETTE.muted }}>{tpl.description}</div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: PALETTE.coral }}>
                <Plus size={11} /> Configure
              </div>
            </button>
          ))}
        </div>
      )}

      {/* CONFIGURATOR + EXECUTION ZONE */}
      {draft && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="contract-configurator">
          <div className="lg:col-span-2 rounded-xl p-4" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}` }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: '#9A4A33' }}>Configuring</div>
                <div className="text-sm font-bold" style={{ color: PALETTE.text }}>{draft.label}</div>
                {draft.recommendation_reason && (
                  <div className="mt-1 text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: PALETTE.lavender + '22', color: '#3F4D7A' }} data-testid="contract-recommendation-reason">
                    <FileSignature size={10} /> {draft.recommendation_reason}
                  </div>
                )}
              </div>
              <button onClick={() => setDraft(null)} className="p-1 rounded-md hover:bg-white" data-testid="contract-cancel-draft" aria-label="Cancel draft">
                <X size={14} style={{ color: PALETTE.muted }} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(draft.template.variables || []).map(v => (
                <label key={v.key} className="text-xs block">
                  <span className="font-semibold" style={{ color: PALETTE.text }}>{v.label}</span>
                  <input
                    type="text"
                    value={draft.variables[v.key] || ''}
                    onChange={e => updateVariable(v.key, e.target.value)}
                    placeholder={v.placeholder || ''}
                    className="w-full mt-1 px-2.5 py-1.5 rounded-md text-sm bg-white"
                    style={{ border: `1px solid ${PALETTE.border}` }}
                    data-testid={`contract-var-${v.key}`}
                  />
                </label>
              ))}
            </div>
            <div className="mt-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: '#9A4A33' }}>Signers</div>
              <div className="space-y-2">
                {draft.signers.map((s, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 rounded-md bg-white" style={{ border: `1px solid ${PALETTE.border}` }} data-testid={`contract-signer-${idx}`}>
                    <div className="text-xs">
                      <div className="font-semibold" style={{ color: PALETTE.text }}>{s.role_label}</div>
                      <div style={{ color: PALETTE.muted }}>{s.role_name}</div>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5">
                      <input type="text" value={s.name} onChange={e => updateSigner(idx, 'name', e.target.value)} placeholder="Full name" className="px-2 py-1.5 rounded-md text-sm" style={{ border: `1px solid ${PALETTE.border}` }} data-testid={`contract-signer-name-${idx}`} />
                      <input type="email" value={s.email} onChange={e => updateSigner(idx, 'email', e.target.value)} placeholder="email@firm.com" className="px-2 py-1.5 rounded-md text-sm" style={{ border: `1px solid ${PALETTE.border}` }} data-testid={`contract-signer-email-${idx}`} />
                    </div>
                  </div>
                ))}
              </div>
              <label className="text-xs block mt-3">
                <span className="font-semibold" style={{ color: PALETTE.text }}>Internal note (optional)</span>
                <textarea value={draft.note} onChange={e => setDraft({ ...draft, note: e.target.value })} rows={2} className="w-full mt-1 px-2.5 py-1.5 rounded-md text-sm bg-white" style={{ border: `1px solid ${PALETTE.border}` }} data-testid="contract-note" />
              </label>
            </div>
          </div>

          {/* EXECUTION ZONE — draggable card + drop target */}
          <div className="lg:col-span-1">
            <div
              draggable={isDraftReady && !sending}
              onDragStart={onDragStart}
              className="rounded-xl p-4 mb-3 select-none"
              style={{
                background: isDraftReady ? `linear-gradient(135deg, ${PALETTE.coral} 0%, #E26A4F 100%)` : '#E5E1EC',
                color: isDraftReady ? '#fff' : PALETTE.muted,
                cursor: isDraftReady ? 'grab' : 'not-allowed',
                opacity: sending ? 0.6 : 1,
              }}
              data-testid="contract-draggable-card"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-90">{isDraftReady ? '✱ Drag me into the zone' : 'Fill all fields to enable'}</div>
              <div className="text-sm font-black mt-1">{draft.label}</div>
              <div className="text-[11px] mt-1 opacity-90">
                {draft.signers.map(s => s.name || s.role_label).filter(Boolean).join(' · ') || '—'}
              </div>
            </div>

            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className="rounded-xl p-4 text-center transition-colors"
              style={{
                border: `2px dashed ${dragging ? PALETTE.coral : PALETTE.lavender}`,
                background: dragging ? PALETTE.coral + '11' : '#fff',
                minHeight: 110,
              }}
              data-testid="contract-execution-zone"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: dragging ? '#9A4A33' : '#3F4D7A' }}>Execution Zone</div>
              <div className="text-xs mt-1" style={{ color: PALETTE.muted }}>
                {sending ? (<><Loader2 size={12} className="inline animate-spin mr-1" /> Dispatching envelope…</>) : 'Drop the configured card to ship the envelope via DocuSign.'}
              </div>
              <button
                onClick={submit}
                disabled={!isDraftReady || sending}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full disabled:opacity-50"
                style={{ background: PALETTE.coral, color: '#fff' }}
                data-testid="contract-send-button"
              >
                <Send size={11} /> Send now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTRACT ROSTER */}
      <div className="mt-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2" style={{ color: PALETTE.muted }}>Active contracts</div>
        {contracts.length === 0 ? (
          <p className="text-xs px-3 py-3 rounded-lg" style={{ background: PALETTE.cream, color: PALETTE.muted }} data-testid="contract-roster-empty">
            No contracts yet. Pick a template above to fire your first envelope.
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="contract-roster">
            {contracts.map(c => {
              const st = STATUS_STYLE[c.status] || { label: c.status, bg: '#F0EDF5', fg: PALETTE.muted };
              const signerCount = (c.signers || []).length;
              return (
                <li key={c.contract_id} className="grid grid-cols-12 items-center gap-3 px-3 py-2 rounded-lg" style={{ background: PALETTE.cream }} data-testid={`contract-${c.contract_id}`}>
                  <div className="col-span-5 min-w-0">
                    <div className="text-sm font-bold truncate" style={{ color: PALETTE.text }}>{c.template_label || c.template_key}</div>
                    <div className="text-xs truncate" style={{ color: PALETTE.muted }}>{fmtDate(c.created_at)} · {signerCount} signer{signerCount === 1 ? '' : 's'} · {c.provider}</div>
                  </div>
                  <div className="col-span-3">
                    <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                  </div>
                  <div className="col-span-4 flex items-center justify-end gap-1.5">
                    {c.status !== 'completed' && (
                      <button
                        onClick={() => markCompleted(c.contract_id)}
                        disabled={completing === c.contract_id}
                        className="text-xs font-semibold px-2 py-1 rounded-md disabled:opacity-50"
                        style={{ background: '#fff', color: PALETTE.muted, border: `1px solid ${PALETTE.border}` }}
                        data-testid={`contract-mark-completed-${c.contract_id}`}
                      >
                        {completing === c.contract_id ? '…' : 'Mark signed'}
                      </button>
                    )}
                    <button
                      onClick={() => downloadPdf(c.contract_id, c.template_key)}
                      className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md"
                      style={{ background: PALETTE.lavender + '33', color: '#3F4D7A' }}
                      data-testid={`contract-download-${c.contract_id}`}
                    >
                      <Download size={11} /> PDF
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default B2BContractEngine;

/**
 * RecommendOfferButton — per-investor closer tool.
 *
 * Reads /api/vault/contracts/recommend/{investor_id} which scores the
 * three DocuSign templates against the investor's telemetry profile and
 * returns a fully-prefilled draft. Click → fire two custom events:
 *   1. `hibff:vault:switch-tab` (detail.tab = 'offers') so the Vault
 *      shell flips to the Offers tab.
 *   2. `hibff:contract-engine:open-draft` (detail = backend draft)
 *      which the mounted <B2BContractEngine> picks up and pre-loads.
 *
 * Renders a compact coral pill that sits next to the Signal Score banner
 * and the InvestorTelemetryChip on every investor card.
 */
export const RecommendOfferButton = ({ investorId, investorName }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await apiClient.request(`/api/vault/contracts/recommend/${investorId}`);
      window.dispatchEvent(new CustomEvent('hibff:vault:switch-tab', { detail: { tab: 'offers' } }));
      // Slight delay so the Offers tab + B2BContractEngine mount before we
      // dispatch the draft payload.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('hibff:contract-engine:open-draft', {
          detail: {
            template_key: res.draft.template_key,
            variables: res.draft.variables,
            signers: res.draft.signers,
            reason: res.draft.reason,
            note: `Recommended for ${investorName || res.investor?.name || investorId}. ${res.draft.reason || ''}`.trim(),
          },
        }));
      }, 60);
    } catch (e) {
      setErr(e.message || 'Recommend failed');
    }
    setBusy(false);
  };

  return (
    <div className="mt-1.5" data-testid={`recommend-offer-${investorId}`}>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold transition-transform hover:scale-[1.02] disabled:opacity-60"
        style={{ background: PALETTE.coral + '22', color: '#9A4A33', border: `1px solid ${PALETTE.coral}66` }}
        data-testid={`recommend-offer-btn-${investorId}`}
      >
        <FileSignature size={11} />
        {busy ? 'Recommending…' : 'Recommend Offer'}
      </button>
      {err && (
        <p className="text-[10px] mt-1" style={{ color: '#7A4040' }} data-testid={`recommend-offer-err-${investorId}`}>{err}</p>
      )}
    </div>
  );
};

